import { useMemo } from 'react';
import { InfiniteData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useGetParkingSharingAvailabilities, useGetParkingSharingRequests } from '_api/useParkingSharing';
import { useOwnProfile } from '_api/useProfile';
import { SafeAreaView } from '_dependencies/safeArea';
import { useAppNavigation, useContextMenuFocusEffect } from '_navigator';
import { track } from '_utils/Amplitude';
import { useBottomSpacer } from '_utils/hooks';
import { flattenIniniteResult } from '_utils/misc';
import { screenMargin, smallestMargin, subtitleFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { HeaderWithNav, HeimeText, QueryView, SelectorChip, Tabs } from 'Components';
import { BookingSharingAvailability, BookingSharingRequest } from 'types/BookingSharing';
import BookingSharingSkeletonLoader from './components/BookingSharingSkeletonLoader';
import NotificationsModal from './components/NotificationsModal';
import BookingSharingItem from '../common/BookingSharingItem';

const BookingSharingCategory = () => {
    const { theme } = useThemeContext();
    const { t } = useTranslation();
    const { data: ownProfile } = useOwnProfile();

    const notificationEnabled = ownProfile?.parking_sharing_availability_creator_notification ?? false;

    useContextMenuFocusEffect(['bookingSharingNewRequest', 'bookingSharingNewAvailability']);

    return (
        <SafeAreaView style={{ height: '100%', backgroundColor: theme.mainBackground }}>
            <HeaderWithNav title={t('bookingSharing:parking_title')} />
            <View
                style={{
                    gap: smallestMargin,
                    marginHorizontal: screenMargin,
                    flexShrink: 0,
                }}
            >
                <View
                    style={{
                        flexWrap: 'wrap-reverse',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: smallestMargin,
                    }}
                >
                    <HeimeText variant="title">{t('bookingSharing:parking_title')}</HeimeText>
                    <SelectorChip
                        containerStyle={{ marginRight: 0 }}
                        variant="lightPrimary"
                        title={t('bookingSharing:shared')}
                    />
                </View>
                <View style={{ flexDirection: 'row', gap: smallestMargin }}>
                    <HeimeText style={{ flex: 1 }} variant="subtitle">
                        {t('bookingSharing:parking_description')}{' '}
                        {notificationEnabled
                            ? t('bookingSharing:parking_notifications_enabled')
                            : t('bookingSharing:parking_notifications_disabled')}
                    </HeimeText>
                    <NotificationsModal parkingNotificationEnabled={notificationEnabled} />
                </View>
            </View>
            <Tabs
                style={{ flex: 1 }}
                tabs={[
                    {
                        title: t('bookingSharing:shares'),
                        content: <TabContent key="availabilities" type="availabilities" />,
                    },
                    {
                        title: t('bookingSharing:requests'),
                        content: <TabContent key="requests" type="requests" />,
                    },
                ]}
            />
        </SafeAreaView>
    );
};

const TabContent = <T extends BookingSharingAvailability | BookingSharingRequest>({
    type,
}: {
    type: 'availabilities' | 'requests';
}) => {
    const { t } = useTranslation();
    const paddingBottom = useBottomSpacer();
    const { navigate } = useAppNavigation();
    const { theme } = useThemeContext();

    const { data, isLoading, error, refetch, isRefetching, fetchNextPage, isFetchingNextPage } =
        // We know what we are doing! Parent guarantees type will not change!
        // eslint-disable-next-line react-hooks/rules-of-hooks
        type === 'requests' ? useGetParkingSharingRequests() : useGetParkingSharingAvailabilities();

    const flatData = useMemo(() => flattenIniniteResult<T>(data as InfiniteData<Array<T>> | undefined), [data]);

    if (isLoading) {
        return <BookingSharingSkeletonLoader type={type} />;
    }

    if (error) {
        throw error;
    }

    const handleCreatePress = () => {
        track(`Pressed create parking sharing ${type}`);
        if (type === 'availabilities') {
            navigate('BookingSharingNew', { variant: 'availability', availabilityToEdit: null });
        } else {
            navigate('BookingSharingNew', { variant: 'request', requestToEdit: null });
        }
    };

    return (
        <Animated.View entering={FadeIn.duration(250)} style={{ flex: 1 }}>
            <QueryView<T>
                style={{ flex: 1 }}
                contentContainerStyle={{
                    paddingBottom,
                    paddingHorizontal: screenMargin,
                }}
                estimatedItemSize={150}
                data={flatData}
                top={
                    flatData.length > 0 ? (
                        <HeimeText
                            style={{
                                fontWeight: 'bold',
                                marginTop: screenMargin,
                                fontSize: subtitleFontSize,
                            }}
                        >
                            {t(`bookingSharing:${type}_count`, { count: flatData.length })}
                        </HeimeText>
                    ) : undefined
                }
                emptyList={
                    <HeimeText
                        variant="subtitle"
                        style={{
                            textAlign: 'center',
                            marginTop: screenMargin,
                            color: theme.darkGray,
                        }}
                    >
                        {t(`bookingSharing:no_${type}`, { type })}{' '}
                        <HeimeText style={{ color: theme.main }} onPress={handleCreatePress}>
                            {t(`bookingSharing:no_${type}_action`)}
                        </HeimeText>
                    </HeimeText>
                }
                renderItem={({ item }) => <BookingSharingItem {...item} />}
                onRefresh={refetch}
                isRefreshing={isRefetching}
                loadMore={fetchNextPage}
                isLoadingMore={isFetchingNextPage}
            />
        </Animated.View>
    );
};

export default BookingSharingCategory;
